import "./EventsPage.css";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode, type RefCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { PanelEventEntry, PanelEventFilters, PanelEventsResponse, PanelModuleState } from "../../panel-contract";
import { EVENT_TONES, type EventTone } from "../../contracts/values";
import { apiErrorText, dashboardCommonTexts, dashboardLanguage, dashboardTexts, eventText, formatNumber, formatTimestamp } from "../locale";
import { moduleName } from "../module-labels";
import { ModuleCount, ModuleHeading } from "../module-panels";
import { useRealtimeEventFeed, type RealtimeFeedState, type RealtimeFeedStatus as RealtimeFeedStatusValue } from "../realtime";
import { useChannelSettings } from "../data/channel-settings";
import { browserTimeZone } from "../ui/format-relative-time";
import { Icon } from "../ui/Icon";
import { ChipGroup, DataTable, EmptyState, InspectorSection, ListDetail, ListToolbar, LoadState as UiLoadState, LoadStateLines, Popover, Select as UiSelect, StatusLed, SubInspector, useInspectorSelection, type QueryError, type SelectOption, type StatusLedState } from "../ui";
import { PanelApiError } from "../api";
import { refreshQuery } from "../data/refresh";
import { dashboardDataKeys } from "../data/keys";
import { useEventHeadQuery, useEventHistoryQuery } from "../data/lists";
import {
  actorLabel,
  affectedPersonLabel,
  chronological,
  emptyEventFilter,
  eventCause,
  eventChipNumber,
  eventDayGroups,
  type EventDayGroup,
  eventDetail,
  eventFilterIsActive,
  eventFeedContinuation,
  eventFeedEntries,
  eventGroupKey,
  eventGroups,
  eventMetadata,
  eventToneFromValue,
  formatEventDetail,
  moderatorLabel,
  moduleLabel,
  MAX_EVENT_HEAD_ENTRIES,
  PERSON_FILTER_DEBOUNCE_MS,
} from "./model";

const mergeEventEntries = (
  current: readonly PanelEventEntry[],
  incoming: readonly PanelEventEntry[],
): PanelEventEntry[] => {
  const byId = new Map<string, PanelEventEntry>();
  for (const entry of current) byId.set(entry.eventId, entry);
  for (const entry of incoming) byId.set(entry.eventId, entry);
  return Array.from(byId.values()).sort((left, right) => chronological(right, left));
};

const appendOlderEntries = (
  current: readonly PanelEventEntry[],
  incoming: readonly PanelEventEntry[],
): PanelEventEntry[] => {
  const existingIds = new Set(current.map((entry) => entry.eventId));
  const existingGroups = new Set(current.map(eventGroupKey));
  const oldest = current.at(-1);
  const olderEntries = mergeEventEntries([], incoming).filter((entry) =>
    !existingIds.has(entry.eventId) && !existingGroups.has(eventGroupKey(entry)) &&
    (oldest === undefined || chronological(entry, oldest) < 0),
  );
  return [...current, ...olderEntries];
};

const appendOlderDayGroups = (
  current: readonly EventDayGroup[],
  incoming: readonly EventDayGroup[],
): EventDayGroup[] => {
  const days = [...current];
  for (const day of incoming) {
    const last = days.at(-1);
    if (last?.key === day.key) {
      days[days.length - 1] = { ...last, groups: [...last.groups, ...day.groups] };
    } else {
      days.push(day);
    }
  }
  return days;
};

const eventPageIdentity = (pageParam: string | null): string => JSON.stringify(pageParam);
const EMPTY_EVENT_HISTORY_PAGES: readonly PanelEventsResponse[] = [];

interface EventDisplaySnapshot {
  entries: readonly PanelEventEntry[];
  days: readonly EventDayGroup[];
  epoch: number;
  processedPages: ReadonlySet<string>;
  paginationIds: ReadonlySet<string>;
  timeZone: string;
}

const actorCell = (entry: PanelEventEntry, texts: ReturnType<typeof dashboardTexts>): ReactNode =>
  entry.actorDisplayName ?? (entry.actorLogin == null
    ? entry.actorUserId == null ? texts.events.automatic : <span className="mono">{entry.actorUserId}</span>
    : `@${entry.actorLogin}`);

const EventChipPair = ({ code, detail, texts: texts }: { code: string; detail: ReturnType<typeof eventDetail>; texts: ReturnType<typeof dashboardTexts> }): ReactElement => {
  const metadata = eventMetadata(code);
  if (metadata === null) {
    return <span className="event-chip-pair"><span className="event-chip" data-tier="outlined">{texts.events.unknown}</span></span>;
  }
  const number = eventChipNumber(detail, metadata.numberKey);
  return <span className="event-chip-pair">
    {number === null ? null : <span className="event-chip event-chip--number">{number}</span>}
    <span className="event-chip" data-family={metadata.family} data-tier={metadata.tier} data-tone={metadata.tone}>{metadata.word[dashboardLanguage()]}</span>
  </span>;
};

/** Copies to the clipboard when it exists (never in a test/jsdom environment) -- same guard as platform.tsx's invitation link. */
const CopyableId = ({ id, texts }: { id: string; texts: ReturnType<typeof dashboardTexts> }): ReactElement => {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = async (): Promise<void> => {
    const clipboard = Reflect.get(navigator, "clipboard") as { writeText: (text: string) => Promise<void> } | undefined;
    if (clipboard === undefined) return;
    await clipboard.writeText(id);
    setCopied(true);
  };
  return <span className="event-inspector-id">
    {id}
    <button type="button" className="button button--quiet" onClick={() => { void copyToClipboard(); }}>{copied ? texts.events.copied : texts.events.copyId}</button>
  </span>;
};

const EventFilterBar = ({
  filters,
  moduleOptions,
  loadedCount,
  onChange,
  queryError,
}: {
  filters: PanelEventFilters;
  moduleOptions: readonly PanelModuleState[];
  loadedCount: number;
  onChange: (filters: PanelEventFilters) => void;
  queryError?: QueryError;
}): ReactElement => {
  const texts = dashboardTexts();
  const [personDraft, setPersonDraft] = useState(filters.person ?? "");
  // The applied filter is the source of truth; the draft follows along when it
  // changes from outside (reset, navigation, second tab). This happens
  // during render instead of in an effect: an effect would show a second
  // pass with a stale value, and React forbids that pattern.
  const [zuletztAngewendet, setZuletztAngewendet] = useState(filters.person);
  if (zuletztAngewendet !== filters.person) {
    setZuletztAngewendet(filters.person);
    setPersonDraft(filters.person ?? "");
  }
  const commitPerson = useCallback((draft: string): void => {
    const value = draft.trim();
    onChange({ ...filters, person: value.length === 0 ? null : value });
  }, [filters, onChange]);
  useEffect(() => {
    if (personDraft.trim() === (filters.person ?? "")) return;
    const timeout = window.setTimeout(() => { commitPerson(personDraft); }, PERSON_FILTER_DEBOUNCE_MS);
    return () => { window.clearTimeout(timeout); };
  }, [commitPerson, filters.person, personDraft]);
  const activeFilter: string[] = [];
  if (filters.origin === "channel") activeFilter.push(texts.events.channelEvents);
  if (filters.origin === "module") activeFilter.push(texts.events.moduleDiagnostics);
  if (filters.module !== null) activeFilter.push(moduleName(filters.module));
  const selectedTones = filters.tones !== undefined && filters.tones.length > 0
    ? filters.tones
    : filters.tone === null ? [] : [filters.tone];
  activeFilter.push(...selectedTones.map((tone) => tone === "info" ? texts.events.info : tone === "warning" ? texts.events.notice : texts.events.error));
  if (filters.person !== null) activeFilter.push(filters.person);
  const moduleSelectOptions: SelectOption[] = moduleOptions.map((module) => ({ value: module.id, label: moduleName(module.id) }));
  const common = dashboardCommonTexts();
  return <ListToolbar
    className="event-filter"
    searchLabel={texts.events.person}
    searchPlaceholder={texts.audit.personPlaceholder}
    searchClearLabel={common.clearSearch}
    searchValue={personDraft}
    onSearchChange={setPersonDraft}
    onSearchKeyDown={(event) => { if (event.key !== "Enter") return; event.preventDefault(); commitPerson(personDraft); }}
    filtersLabel={texts.events.filter}
    filters={<>
      <div className="event-filter__group">
        <span className="event-filter__group-label">{texts.events.type}</span>
        <ChipGroup
          className="event-filter__chips"
          ariaLabel={texts.events.type}
          value={filters.origin}
          onChange={(value) => { onChange({ ...filters, origin: value === "channel" || value === "module" ? value : null }); }}
          options={[
            { value: "", label: texts.events.all },
            { value: "channel", label: texts.events.channelEvents },
            { value: "module", label: texts.events.moduleDiagnostics },
          ]}
        />
      </div>
      <div className="event-filter__group">
        <span className="event-filter__group-label">{texts.events.severity}</span>
        <ChipGroup
          className="event-filter__chips"
          ariaLabel={texts.events.severity}
          {...(selectedTones.length > 1 ? {
            value: null,
            onChange: () => undefined,
            selectedValues: selectedTones,
            onSelectedValuesChange: (values: readonly string[]) => {
              const tones = values.filter((tone): tone is EventTone => EVENT_TONES.includes(tone as EventTone));
              const nextFilters = { ...filters, tone: tones.length === 1 ? tones[0] ?? null : null };
              delete nextFilters.tones;
              if (tones.length > 1) nextFilters.tones = tones;
              onChange(nextFilters);
            },
            options: [
              { value: "error", label: texts.events.error },
              { value: "warning", label: texts.events.notice },
              { value: "info", label: texts.events.info },
            ],
          } : {
            value: filters.tone,
            onChange: (value: string | null) => {
              const nextFilters = { ...filters, tone: eventToneFromValue(value ?? "") };
              delete nextFilters.tones;
              onChange(nextFilters);
            },
            options: [
              { value: "", label: texts.events.all },
              { value: "error", label: texts.events.error },
              { value: "warning", label: texts.events.notice },
              { value: "info", label: texts.events.info },
            ],
          })}
        />
      </div>
      <UiSelect
        label={texts.events.moduleFilter}
        value={filters.module ?? ""}
        onChange={(value) => { onChange({ ...filters, module: value === "" ? null : value }); }}
        options={[{ value: "", label: texts.events.allModules }, ...moduleSelectOptions]}
      />
    </>}
    usage={{ count: loadedCount, loaded: true, copy: { countSuffix: "", filteredInfix: common.of, filteredSuffix: "", limitInfix: common.of, limitSuffix: "", loadedSuffix: common.loaded } }}
    {...(queryError === undefined ? {} : { queryError })}
    {...(activeFilter.length === 0 ? {} : { activeFilters: activeFilter.join(" · "), activeFiltersLabel: texts.events.activeFilters, resetLabel: texts.events.resetFilters, onReset: () => { setPersonDraft(""); onChange(emptyEventFilter); } })}
  />;
};

const EventFeedEnd = ({
  nextCursor,
  loadingNextPage,
  onNextPage,
}: {
  nextCursor: string | null;
  loadingNextPage: boolean;
  onNextPage: () => void;
}): ReactElement => {
  const feedEndRef = useRef<HTMLSpanElement | null>(null);
  const loadNextPage = useCallback((): void => {
    if (nextCursor !== null && !loadingNextPage) onNextPage();
  }, [loadingNextPage, nextCursor, onNextPage]);
  useEffect(() => {
    if (nextCursor === null) return;
    const scrollRoot = document.querySelector<HTMLElement>(".dashboard-shell__main");
    const onScroll = (): void => {
      const scrollTop = scrollRoot?.scrollTop ?? window.scrollY;
      const clientHeight = scrollRoot?.clientHeight ?? window.innerHeight;
      const scrollHeight = scrollRoot?.scrollHeight ?? document.documentElement.scrollHeight;
      if (clientHeight + scrollTop >= scrollHeight - 1) loadNextPage();
    };
    const scrollTarget: HTMLElement | Window = scrollRoot ?? window;
    scrollTarget.addEventListener("scroll", onScroll, { passive: true });
    const end = feedEndRef.current;
    if (end !== null && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) loadNextPage();
      }, { root: scrollRoot });
      observer.observe(end);
      return () => { observer.disconnect(); scrollTarget.removeEventListener("scroll", onScroll); };
    }
    return () => { scrollTarget.removeEventListener("scroll", onScroll); };
  }, [loadNextPage, nextCursor]);
  return <span
    ref={feedEndRef}
    className="event-feed__end"
    aria-hidden="true"
  />;
};

const realtimeFeedState = (status: RealtimeFeedStatusValue): StatusLedState =>
  status === "connected" ? "ok" : status === "renew" ? "error" : status === "offline" ? "off" : "warn";

const RealtimeFeedStatus = ({ status }: { status: RealtimeFeedStatusValue }): ReactElement => {
  const texts = dashboardTexts();
  const label = status === "connected"
    ? texts.events.realtimeConnected
    : status === "connecting"
      ? texts.events.realtimeConnecting
      : status === "reconnecting"
        ? texts.events.realtimeReconnecting
        : status === "renew" ? texts.events.realtimeRenewSession : texts.events.realtimeOffline;
  return <span className="realtime-status" aria-live="polite"><StatusLed state={realtimeFeedState(status)} label={label} />{status === "renew" ? <a className="profile-link" href="/auth/login">{texts.events.realtimeRenewSession}</a> : null}</span>;
};

export const EventsPage = ({ channelId, filters, moduleOptions, onFiltersChange }: {
  channelId: string;
  filters: PanelEventFilters;
  moduleOptions: readonly PanelModuleState[];
  onFiltersChange: (filters: PanelEventFilters) => void;
}): ReactElement => {
  const followingRef = useRef(true);
  const head = useEventHeadQuery(channelId, filters, false, () => followingRef.current);
  const history = useEventHistoryQuery(channelId, filters, head.data);
  const settingsQuery = useChannelSettings(channelId);
  const timeZoneFallback = settingsQuery.isError;
  const channelTimeZone = timeZoneFallback ? browserTimeZone() : settingsQuery.data?.timeZone ?? null;
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => dashboardDataKeys.eventHead(channelId, filters), [channelId, filters]);
  const identityKey = JSON.stringify(queryKey);
  const pages = history.data?.pages ?? EMPTY_EVENT_HISTORY_PAGES;
  const pageParams = (history.data?.pageParams ?? []) as (string | null)[];
  const entries = useMemo(() => eventFeedEntries(head.data, pages), [head.data, pages]);
  const continuation = eventFeedContinuation(head.data, pages);
  const queuedRefreshEpoch = useRef<number | null>(null);
  const requestHeadRefresh = useCallback((force = false): void => {
    if (queryClient.getQueryState(queryKey)?.fetchStatus === "fetching") {
      queuedRefreshEpoch.current = head.data?.epoch ?? 0;
      return;
    }
    if (!force && !head.isStale && (head.data?.entries.length ?? 0) <= MAX_EVENT_HEAD_ENTRIES) return;
    void refreshQuery(queryClient, queryKey);
  }, [head.data, head.isStale, queryClient, queryKey]);
  useEffect(() => {
    const queuedEpoch = queuedRefreshEpoch.current;
    if (queuedEpoch === null || head.isFetching) return;
    queuedRefreshEpoch.current = null;
    if (head.data?.epoch !== queuedEpoch) return;
    void refreshQuery(queryClient, queryKey);
  }, [head.data?.epoch, head.isFetching, queryClient, queryKey]);
  const refreshOnReturn = useCallback((): void => { requestHeadRefresh(true); }, [requestHeadRefresh]);
  const onBecameFollowing = useCallback((): void => {
    if ((head.data?.entries.length ?? 0) > MAX_EVENT_HEAD_ENTRIES) requestHeadRefresh(true);
  }, [head.data?.entries.length, requestHeadRefresh]);
  const loadNextPage = useCallback((): void => {
    if (history.isFetching || continuation === null || head.data === undefined) return;
    void history.fetchNextPage({ cancelRefetch: false });
  }, [continuation, head.data, history]);
  // History is a disabled query, so only fetchNextPage retries the failed page; loaded pages stay.
  const retry = useCallback((): void => {
    if (history.isError) void history.fetchNextPage({ cancelRefetch: false });
    if (head.isError || !history.isError) requestHeadRefresh(true);
  }, [head.isError, history, requestHeadRefresh]);
  const errorValue = head.error ?? history.error;
  const error = errorValue instanceof PanelApiError
    ? apiErrorText(errorValue.code, dashboardTexts().errors.dataLoadFailed)
    : head.isError || history.isError ? dashboardTexts().errors.dataLoadFailed : null;
  return <EventsPageContent
    identityKey={identityKey}
    channelId={channelId}
    channelTimeZone={channelTimeZone}
    timeZoneFallback={timeZoneFallback}
    filters={filters}
    moduleOptions={moduleOptions}
    onFiltersChange={onFiltersChange}
    entries={entries}
    epoch={head.data?.epoch ?? 0}
    nextCursor={continuation}
    loading={head.isPending}
    fetching={head.isFetching}
    queryStale={head.isStale}
    error={error}
    loadingNextPage={history.isFetching}
    pages={pages}
    pageParams={pageParams}
    onRefresh={retry}
    onRefreshOnReturn={refreshOnReturn}
    onBecameFollowing={onBecameFollowing}
    onNextPage={loadNextPage}
    followingRef={followingRef}
  />;
};

interface EventsPageContentProperties {
  identityKey: string;
  channelId: string;
  channelTimeZone: string | null;
  timeZoneFallback: boolean;
  entries: readonly PanelEventEntry[];
  pages: readonly PanelEventsResponse[];
  pageParams: readonly (string | null)[];
  epoch: number;
  nextCursor: string | null;
  loading: boolean;
  fetching: boolean;
  queryStale: boolean;
  error: string | null;
  filters: PanelEventFilters;
  moduleOptions: readonly PanelModuleState[];
  onFiltersChange: (filters: PanelEventFilters) => void;
  onRefresh: () => void;
  onRefreshOnReturn: () => void;
  onBecameFollowing: () => void;
  onNextPage: () => void;
  loadingNextPage: boolean;
  followingRef: { current: boolean };
}

const EventsPageContent = (properties: EventsPageContentProperties): ReactElement => {
  const { identityKey, channelId, filters, onRefresh, onRefreshOnReturn, onBecameFollowing, followingRef, ...contentProperties } = properties;
  const feedRef = useRef<HTMLDivElement | null>(null);
  const setFeedRef = useCallback<RefCallback<HTMLDivElement>>((feed) => { feedRef.current = feed; }, []);
  const [readerAtTop, setReaderAtTop] = useState(true);
  const atBeginning = useCallback((): boolean => {
    const feed = feedRef.current;
    if (feed === null) return true;
    const scrollRoot = feed.closest<HTMLElement>(".dashboard-shell__main");
    if (scrollRoot === null) return window.scrollY <= feed.getBoundingClientRect().top + window.scrollY + 8;
    const feedStart = feed.getBoundingClientRect().top - scrollRoot.getBoundingClientRect().top + scrollRoot.scrollTop;
    return scrollRoot.scrollTop <= feedStart + 8;
  }, []);
  useEffect(() => {
    const updateReaderPosition = (): void => {
      const isFollowing = atBeginning();
      const becameFollowing = isFollowing && !followingRef.current;
      followingRef.current = isFollowing;
      setReaderAtTop(isFollowing);
      if (becameFollowing) onBecameFollowing();
    };
    updateReaderPosition();
    const scrollRoot = document.querySelector<HTMLElement>(".dashboard-shell__main") ??
      feedRef.current?.closest<HTMLElement>(".dashboard-shell__main") ?? null;
    const scrollTarget: HTMLElement | Window = scrollRoot ?? window;
    scrollTarget.addEventListener("scroll", updateReaderPosition, { passive: true });
    return () => { scrollTarget.removeEventListener("scroll", updateReaderPosition); };
  }, [atBeginning, followingRef, onBecameFollowing]);
  const scrollToBeginning = useCallback((): void => {
    const feed = feedRef.current;
    if (feed === null) return;
    const scrollRoot = feed.closest<HTMLElement>(".dashboard-shell__main");
    if (scrollRoot === null) {
      const feedStart = feed.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, feedStart), behavior: "auto" });
    } else {
      const feedStart = feed.getBoundingClientRect().top - scrollRoot.getBoundingClientRect().top + scrollRoot.scrollTop;
      scrollRoot.scrollTo({ top: Math.max(0, feedStart), behavior: "auto" });
    }
    followingRef.current = true;
    setReaderAtTop(true);
  }, [followingRef]);
  const realtime = useRealtimeEventFeed({
    channelId,
    refreshOnReturn: onRefreshOnReturn,
    scrollToBeginning,
  });
  return <EventsPageFilterState
    {...contentProperties}
    identityKey={identityKey}
    filters={filters}
    readerAtTop={readerAtTop}
    onRefresh={onRefresh}
    feedRef={setFeedRef}
    realtime={realtime}
  />;
};

const EventsPageFilterState = ({
  identityKey,
  channelTimeZone,
  timeZoneFallback,
  entries,
  pages,
  pageParams,
  epoch,
  readerAtTop,
  nextCursor,
  loading,
  fetching,
  queryStale,
  error,
  filters,
  moduleOptions,
  onFiltersChange,
  onRefresh,
  onNextPage,
  loadingNextPage,
  feedRef,
  realtime,
}: Omit<EventsPageContentProperties, "channelId" | "onRefreshOnReturn" | "onBecameFollowing" | "followingRef"> & {
  readerAtTop: boolean;
  feedRef: RefCallback<HTMLDivElement>;
  realtime: RealtimeFeedState;
}): ReactElement => {
  const texts = dashboardTexts();
  const groupingTimeZone = channelTimeZone ?? "UTC";
  const [snapshotIdentityKey, setSnapshotIdentityKey] = useState(identityKey);
  const [snapshot, setSnapshot] = useState<EventDisplaySnapshot>(() => ({
    entries,
    days: eventDayGroups(eventGroups(entries), groupingTimeZone),
    epoch,
    processedPages: new Set(pageParams.map(eventPageIdentity)),
    paginationIds: new Set(),
    timeZone: groupingTimeZone,
  }));
  const [followIdentityQuery, setFollowIdentityQuery] = useState(fetching || queryStale);
  const [wasReaderAtTop, setWasReaderAtTop] = useState(readerAtTop);
  const identityChanged = snapshotIdentityKey !== identityKey;
  if (identityChanged) {
    setSnapshotIdentityKey(identityKey);
    setSnapshot({
      entries,
      days: eventDayGroups(eventGroups(entries), groupingTimeZone),
      epoch,
      processedPages: new Set(pageParams.map(eventPageIdentity)),
      paginationIds: new Set(),
      timeZone: groupingTimeZone,
    });
    setFollowIdentityQuery(true);
  }
  let followsIdentityQuery = followIdentityQuery;
  if (readerAtTop && !wasReaderAtTop) {
    setWasReaderAtTop(true);
  } else if (!readerAtTop && wasReaderAtTop) {
    setWasReaderAtTop(false);
    followsIdentityQuery = false;
    if (followIdentityQuery) setFollowIdentityQuery(false);
  }
  const followsQuery = identityChanged || readerAtTop || followsIdentityQuery;
  if (followsQuery) {
    const allPagesProcessed = pageParams.every((pageParam) => snapshot.processedPages.has(eventPageIdentity(pageParam))) &&
      snapshot.processedPages.size === pageParams.length;
    if (snapshot.entries !== entries || snapshot.epoch !== epoch || !allPagesProcessed || snapshot.timeZone !== groupingTimeZone) {
      setSnapshot({
        entries,
        days: eventDayGroups(eventGroups(entries), groupingTimeZone),
        epoch,
        processedPages: new Set(pageParams.map(eventPageIdentity)),
        paginationIds: new Set(),
        timeZone: groupingTimeZone,
      });
    }
    if (followIdentityQuery && !fetching) setFollowIdentityQuery(false);
  } else if (snapshot.epoch !== epoch) {
    // A frozen reader adopts the pending epoch by clicking jump or manually scrolling back to the top.
  } else if (snapshot.timeZone !== groupingTimeZone) {
    setSnapshot({ ...snapshot, days: eventDayGroups(eventGroups(snapshot.entries), groupingTimeZone), timeZone: groupingTimeZone });
  } else {
    const newPageIndexes = pageParams.flatMap((pageParam, index) =>
      snapshot.processedPages.has(eventPageIdentity(pageParam)) ? [] : [index],
    );
    if (newPageIndexes.length > 0) {
      const loadedOlderEntries = newPageIndexes.flatMap((index) => pages[index]?.entries ?? []);
      const entriesWithOlderPage = appendOlderEntries(snapshot.entries, loadedOlderEntries);
      const existingIds = new Set(snapshot.entries.map((entry) => entry.eventId));
      const newlyAddedEntries = entriesWithOlderPage.filter((entry) => !existingIds.has(entry.eventId));
      const olderDays = eventDayGroups(eventGroups(newlyAddedEntries), groupingTimeZone);
      const processedPages = new Set(snapshot.processedPages);
      for (const index of newPageIndexes) {
        const pageParam = pageParams[index];
        if (pageParam !== undefined) processedPages.add(eventPageIdentity(pageParam));
      }
      const paginationIds = new Set(snapshot.paginationIds);
      for (const entry of loadedOlderEntries) paginationIds.add(entry.eventId);
      setSnapshot({
        entries: entriesWithOlderPage,
        days: appendOlderDayGroups(snapshot.days, olderDays),
        epoch,
        processedPages,
        paginationIds,
        timeZone: groupingTimeZone,
      });
    }
  }
  const displayedEntries = followsQuery ? entries : snapshot.entries;
  const displayedIds = new Set(displayedEntries.map((entry) => entry.eventId));
  const pendingCount = readerAtTop ? 0 : entries.filter((entry) =>
    !displayedIds.has(entry.eventId) && !snapshot.paginationIds.has(entry.eventId),
  ).length;
  const { selectedKey: selectedGroupKey, select: selectGroup, rowRef: groupRowRef, close: closeGroup } = useInspectorSelection<string>(identityKey);
  const dayGroups = followsQuery ? eventDayGroups(eventGroups(displayedEntries), groupingTimeZone) : snapshot.days;
  const groups = followsQuery ? eventGroups(displayedEntries) : dayGroups.flatMap((day) => day.groups);
  const selectedGroup = groups.find((group) => group.key === selectedGroupKey) ?? null;
  const selectedHistory = selectedGroup === null ? [] : [...selectedGroup.entries].sort(chronological);
  const triggerNames = Array.from(new Set(selectedHistory.map((entry) => actorLabel(entry, texts))));
  const moderatorNames = Array.from(new Set(
    selectedHistory.map((entry) => moderatorLabel(eventDetail(entry.detail, entry.code))).filter((name): name is string => name !== null),
  ));
  const affectedNames = Array.from(new Set(
    selectedHistory.map((entry) => affectedPersonLabel(eventDetail(entry.detail, entry.code))).filter((name): name is string => name !== null),
  ));
  const eventEntries = displayedEntries;
  const pendingEpoch = !readerAtTop && snapshot.epoch !== epoch;
  const eventFeedEndCursor = pendingEpoch ? null : nextCursor;
  const filterActive = eventFilterIsActive(filters);
  const selectedTones = filters.tones !== undefined && filters.tones.length > 0
    ? filters.tones
    : filters.tone === null ? [] : [filters.tone];
  return (
    <>
      <ModuleHeading kind="events" title={texts.events.title} subtitle={loading && eventEntries.length === 0
        ? <span className="module-heading__subtitle-placeholder" aria-hidden="true"><ModuleCount count={0} label={texts.events.count} /></span>
        : <ModuleCount count={eventEntries.length} label={texts.events.count} />} actions={<RealtimeFeedStatus status={realtime.status} />} />
      <ListDetail
        onCloseInspector={closeGroup}
        list={
          <section className="content-section events-page__content" aria-label={texts.events.log}>
            <EventFilterBar
              filters={filters}
              moduleOptions={moduleOptions}
              loadedCount={eventEntries.length}
              onChange={onFiltersChange}
              {...(error === null || eventEntries.length === 0 ? {} : { queryError: {
                title: texts.events.connectionLost,
                message: error,
                onRetry: onRefresh,
              } })}
            />
            <div className="realtime-feed__notice-slot" data-pending={pendingCount > 0}>
              {pendingCount === 0 ? null : <button className="button button--with-icon realtime-feed__notice" type="button" onClick={realtime.jumpToBeginning} aria-live="polite"><Icon name="jumpToTop" size={16} />{texts.events.realtimeNew(formatNumber(pendingCount))}</button>}
            </div>
            <UiLoadState
              variant="panel-420"
              status={loading && eventEntries.length === 0
                ? "loading"
                : error !== null && eventEntries.length === 0 ? "error"
                : eventEntries.length === 0 ? "empty" : "success"}
              loading={<LoadStateLines rows={50} height={34} />}
              empty={filterActive ? (
                <EmptyState
                  title={texts.events.noMatches}
                  description={`${texts.events.activeFilters} ${[
                    filters.origin === "channel" ? texts.events.channelEvents : null,
                    filters.origin === "module" ? texts.events.moduleDiagnostics : null,
                    filters.module === null ? null : moduleName(filters.module),
                    ...selectedTones.map((tone) => tone === "info" ? texts.events.info : tone === "warning" ? texts.events.notice : texts.events.error),
                    filters.person,
                  ].filter((value): value is string => value !== null).join(" · ")}`}
                  action={{ label: texts.events.resetFilters, onClick: () => { onFiltersChange(emptyEventFilter); } }}
                />
              ) : <p className="empty-state">{texts.events.none}</p>}
              error={<p role="alert">{error ?? texts.events.load}</p>}
              queryError={{ title: texts.events.connectionLost, message: error ?? texts.events.load, onRetry: onRefresh }}
            >
              <>
              <div ref={feedRef} className="event-feed">
                <div key={identityKey} className={fetching && followsQuery ? "stale" : undefined} aria-busy={fetching && followsQuery}>
                  <DataTable
                    ariaLabel={texts.events.log}
                    className="event-table"
                    rows={groups.map((group) => ({
                      ...group,
                      id: group.key,
                      at: group.newestEntry.createdAt,
                      moduleGroup: group.representative.moduleId,
                    }))}
                    timeZone={channelTimeZone}
                    {...(timeZoneFallback ? { timeZoneFallbackLabel: texts.streamManager.browserTimeZoneFallback } : {})}
                    emptyLabel={texts.events.none}
                    selectedId={selectedGroupKey}
                    onSelect={selectGroup}
                    rowRef={groupRowRef}
                    columns={[
                      {
                        key: "event",
                        label: texts.events.event,
                        render: (group) => {
                          const entry = group.representative;
                          const eventLabel = eventText(entry.code, eventDetail(entry.detail, entry.code));
                          const cause = eventCause(entry);
                          return <>
                            <span className="event-label event-table__primary">
                              <EventChipPair code={entry.code} detail={eventDetail(entry.detail, entry.code)} texts={texts} />
                              <span className={`event-table__text${eventMetadata(entry.code) === null ? " mono" : ""}`} title={eventLabel}>{eventLabel}</span>
                              {cause === null ? null : (
                                <Popover triggerLabel={texts.events.showCause(eventLabel)} icon="cause">
                                  <span className="event-cause">
                                    <span>{cause.text}</span>
                                    {cause.message === null ? null : (
                                      <span className="event-cause__message">
                                        {cause.messageIsFromTwitch ? texts.events.causeTwitchMessage(cause.message) : texts.events.causeDetailMessage(cause.message)}
                                      </span>
                                    )}
                                  </span>
                                </Popover>
                              )}
                            </span>
                            <span className="event-table__mobile-meta muted">
                              <span className="data-table__module-group" data-module-group={entry.moduleId} aria-hidden="true" />
                              <span className={moduleLabel(entry) === entry.moduleId ? "mono" : undefined} title={moduleLabel(entry)}>{moduleLabel(entry)}</span>
                              <span aria-hidden="true">·</span>
                              <span title={actorLabel(entry, texts)}>{actorCell(entry, texts)}</span>
                            </span>
                          </>;
                        },
                      },
                      {
                        key: "module",
                        label: texts.events.module,
                        moduleGroup: true,
                        hideOnMobile: true,
                        render: (group) => {
                          const entry = group.representative;
                          const label = moduleLabel(entry);
                          return <span className={label === entry.moduleId ? "mono" : undefined} title={label}>{label}</span>;
                        },
                      },
                      {
                        key: "actor",
                        label: texts.events.who,
                        hideOnMobile: true,
                        render: (group) => {
                          const entry = group.representative;
                          return <span title={actorLabel(entry, texts)}>{actorCell(entry, texts)}</span>;
                        },
                      },
                    ]}
                  />
                </div>
                <EventFeedEnd nextCursor={eventFeedEndCursor} loadingNextPage={loadingNextPage} onNextPage={onNextPage} />
              </div>
              <div className="events-page__pagination-slot">
                {loading || eventEntries.length === 0 ? null : pendingEpoch
                  ? <button className="button button--secondary" type="button" onClick={realtime.jumpToBeginning}>{texts.events.jumpToLatest}</button>
                  : nextCursor === null
                  ? <p className="events-page__feed-end">{texts.events.feedEnd}</p>
                  : <button className="button button--secondary" type="button" onClick={onNextPage} disabled={loadingNextPage}>{loadingNextPage ? texts.events.loadingOlder : texts.events.loadOlder}</button>}
              </div>
              </>
            </UiLoadState>
          </section>
        }
        inspector={selectedGroup === null ? null : (
          <SubInspector
            ariaLabel={texts.events.detail}
            title={texts.events.operation}
            identifier={selectedGroup.representative.triggerId || selectedGroup.representative.eventId}
            closeLabel={dashboardCommonTexts().close}
            onClose={closeGroup}
          >
            <InspectorSection title={texts.events.operation}><dl className="properties">
              <div><dt>{texts.events.timestamp}</dt><dd className="mono" title={selectedHistory[0]?.createdAt}>{selectedHistory[0] === undefined ? "" : formatTimestamp(selectedHistory[0].createdAt)}</dd></div>
              <div><dt>{texts.events.module}</dt><dd>{Array.from(new Set(selectedHistory.map(moduleLabel))).join(", ")}</dd></div>
              <div><dt>{texts.events.trigger}</dt><dd>{triggerNames.join(", ")}</dd></div>
              {moderatorNames.length === 0 ? null : <div><dt>{texts.events.moderator}</dt><dd>{moderatorNames.join(", ")}</dd></div>}
              {affectedNames.length === 0 ? null : <div><dt>{texts.events.affectedPerson}</dt><dd>{affectedNames.join(", ")}</dd></div>}
            </dl></InspectorSection>
            <InspectorSection title={texts.events.history}>
            <ol className="event-history">{selectedHistory.map((entry) => {
              return <li key={entry.eventId}>
                <div className="event-history__heading">
                  <span className="event-label"><EventChipPair code={entry.code} detail={eventDetail(entry.detail, entry.code)} texts={texts} /><span className={eventMetadata(entry.code) === null ? "mono" : undefined}>{eventText(entry.code, eventDetail(entry.detail, entry.code))}</span></span>
                  <span className="mono muted">{entry.code}</span>
                </div>
                <details>
                  <summary>{texts.events.technicalDetails}</summary>
                  <CopyableId id={entry.triggerId || entry.eventId} texts={texts} />
                  <pre className="event-detail-json">{formatEventDetail(entry.detail, entry.code)}</pre>
                </details>
              </li>;
            })}</ol>
            </InspectorSection>
          </SubInspector>
        )}
      />
    </>
  );
};
